import { useState } from "react";
import { GripHorizontal, ListChecks, Rows3 } from "lucide-react";
import {
  formatItDateTime,
  getItStateLabel,
  type ItEstado,
  type ItGroup,
  type ItIncident,
} from "../types/it";

type Props = {
  groups: ItGroup[];
  canReorder: boolean;
  onOpenIncident: (incident: ItIncident) => void;
  onReorder: (ids: number[]) => Promise<void>;
};

const stateTone: Partial<
  Record<
    ItEstado,
    {
      header: string;
      icon: string;
      text: string;
      orb: string;
      border: string;
      row: string;
      badge: string;
      line: string;
    }
  >
> = {
  solicitada: {
    header: "bg-[linear-gradient(135deg,#ffffff_0%,#f8fbf9_100%)]",
    icon: "border-[#00d09c]/18 bg-[#00d09c]/8 text-[#0b4a2f]",
    text: "text-[#0b4a2f]",
    orb: "bg-[#00d09c]/12",
    border: "border-[#dfe8e4]",
    row: "hover:bg-[#00d09c]/6",
    badge: "border-[#00d09c]/18 bg-[#00d09c]/8 text-[#0b4a2f]",
    line: "from-[#0b4a2f] to-[#00d09c]",
  },
  aprobada: {
    header: "bg-[linear-gradient(135deg,#ffffff_0%,#f8fbf9_100%)]",
    icon: "border-[#00d09c]/18 bg-white text-[#0b4a2f]",
    text: "text-[#0b4a2f]",
    orb: "bg-[#00d09c]/10",
    border: "border-[#dfe8e4]",
    row: "hover:bg-[#00d09c]/6",
    badge: "border-[#00d09c]/18 bg-[#00d09c]/8 text-[#0b4a2f]",
    line: "from-[#0b4a2f] to-[#00a879]",
  },
  creada: {
    header: "bg-[linear-gradient(135deg,#ffffff_0%,#f8fafc_100%)]",
    icon: "border-slate-200 bg-slate-50 text-slate-700",
    text: "text-slate-700",
    orb: "bg-slate-300/18",
    border: "border-slate-200",
    row: "hover:bg-slate-50",
    badge: "border-slate-200 bg-slate-50 text-slate-700",
    line: "from-slate-800 to-[#0b4a2f]",
  },
  iniciada: {
    header: "bg-[linear-gradient(135deg,#ffffff_0%,#f7fbf8_100%)]",
    icon: "border-[#00d09c]/16 bg-[#00d09c]/7 text-[#0b4a2f]",
    text: "text-[#0b4a2f]",
    orb: "bg-[#00d09c]/10",
    border: "border-[#dfe8e4]",
    row: "hover:bg-[#00d09c]/6",
    badge: "border-[#00d09c]/18 bg-[#00d09c]/8 text-[#0b4a2f]",
    line: "from-slate-800 to-[#00a879]",
  },
  corregida: {
    header: "bg-[linear-gradient(135deg,#ffffff_0%,#f8fbf9_100%)]",
    icon: "border-[#00c3af]/18 bg-[#00c3af]/8 text-[#0b4a2f]",
    text: "text-[#0b4a2f]",
    orb: "bg-[#00c3af]/10",
    border: "border-[#dfe8e4]",
    row: "hover:bg-[#00c3af]/6",
    badge: "border-[#00c3af]/18 bg-[#00c3af]/8 text-[#0b4a2f]",
    line: "from-[#0b4a2f] to-[#00c3af]",
  },
  rechazada: {
    header: "bg-[linear-gradient(135deg,#ffffff_0%,#fff1f2_100%)]",
    icon: "border-rose-100 bg-rose-50 text-rose-700",
    text: "text-rose-700",
    orb: "bg-rose-300/18",
    border: "border-rose-200",
    row: "hover:bg-rose-50/45",
    badge: "border-rose-100 bg-rose-50 text-rose-700",
    line: "from-rose-500 to-amber-400",
  },
  cerrada: {
    header: "bg-[linear-gradient(135deg,#ffffff_0%,#f8fafc_100%)]",
    icon: "border-slate-200 bg-slate-50 text-slate-700",
    text: "text-slate-700",
    orb: "bg-slate-300/18",
    border: "border-slate-300",
    row: "hover:bg-slate-50",
    badge: "border-slate-200 bg-slate-50 text-slate-700",
    line: "from-slate-800 to-slate-500",
  },
  incidencia_rechazada: {
    header: "bg-[linear-gradient(135deg,#ffffff_0%,#fef2f2_100%)]",
    icon: "border-red-100 bg-red-50 text-red-700",
    text: "text-red-700",
    orb: "bg-red-300/16",
    border: "border-red-200",
    row: "hover:bg-red-50/45",
    badge: "border-red-100 bg-red-50 text-red-700",
    line: "from-red-500 to-rose-400",
  },
};

const fallbackTone = {
  header: "bg-[linear-gradient(135deg,#ffffff_0%,#f8fbf9_100%)]",
  icon: "border-[#dfe8e4] bg-white text-[#0b4a2f]",
  text: "text-[#0b4a2f]",
  orb: "bg-[#00d09c]/10",
  border: "border-[#dfe8e4]",
  row: "hover:bg-[#00d09c]/6",
  badge: "border-[#00d09c]/18 bg-[#00d09c]/8 text-[#0b4a2f]",
  line: "from-[#0b4a2f] to-[#00d09c]",
};

function getGroupTone(groupKey: string) {
  return stateTone[groupKey as ItEstado] ?? fallbackTone;
}

export function ItKanbanBoard({
  groups,
  canReorder,
  onOpenIncident,
  onReorder,
}: Props) {
  const [dragState, setDragState] = useState<{
    incidentId: number;
    groupKey: string;
  } | null>(null);

  const [dropTarget, setDropTarget] = useState<{
    groupKey: string;
    incidentId: number;
    position: "before" | "after";
  } | null>(null);

  const visibleGroups = groups.filter((group) => group.items.length > 0);

  const moveWithinGroup = async (
    group: ItGroup,
    targetId: number | null,
    position: "before" | "after" | "end",
  ) => {
    if (!dragState || dragState.groupKey !== group.key) return;

    const ids = group.items.map((item) => item.id);
    const fromIndex = ids.indexOf(dragState.incidentId);
    if (fromIndex === -1) return;

    const next = [...ids];
    next.splice(fromIndex, 1);

    if (position === "end" || targetId === null) {
      next.push(dragState.incidentId);
    } else {
      const targetIndex = next.indexOf(targetId);
      if (targetIndex === -1) return;

      const insertAt = position === "before" ? targetIndex : targetIndex + 1;
      next.splice(insertAt, 0, dragState.incidentId);
    }

    if (next.join(",") === ids.join(",")) return;

    setDropTarget(null);
    setDragState(null);
    await onReorder(next);
  };

  if (visibleGroups.length === 0) {
    return (
      <section className="dashboard-scale-in rounded-[34px] border border-dashed border-[#dfe8e4] bg-white/85 p-10 text-center shadow-[0_24px_70px_rgba(15,23,42,.08)] backdrop-blur-xl">
        <div className="mx-auto grid h-14 w-14 place-items-center rounded-2xl border border-[#00d09c]/18 bg-[#00d09c]/8 text-[#0b4a2f]">
          <Rows3 size={22} />
        </div>

        <div className="mt-4 text-lg font-black text-slate-950">
          Sin incidencias visibles
        </div>

        <p className="mx-auto mt-2 max-w-md text-sm font-semibold leading-6 text-slate-500">
          Ajusta los filtros o revisa otro estado para continuar el seguimiento.
        </p>
      </section>
    );
  }

  return (
    <div className="space-y-5">
      {visibleGroups.map((group, index) => {
        const tone = getGroupTone(group.key);

        return (
          <section
            key={group.key}
            className={[
              "dashboard-scale-in overflow-hidden rounded-[34px] border bg-white/94 shadow-[0_24px_70px_rgba(15,23,42,.09)] backdrop-blur-xl transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_34px_90px_rgba(15,23,42,.12)]",
              tone.border,
            ].join(" ")}
            style={{ animationDelay: `${index * 70}ms` }}
          >
            <header
              className={[
                "relative overflow-hidden border-b px-5 py-4",
                tone.border,
                tone.header,
              ].join(" ")}
            >
              <div
                className={[
                  "pointer-events-none absolute -right-16 top-[-70px] h-40 w-40 rounded-full blur-3xl",
                  tone.orb,
                ].join(" ")}
              />

              <span
                className={[
                  "absolute inset-x-5 top-0 h-1 rounded-b-full bg-gradient-to-r",
                  tone.line,
                ].join(" ")}
              />

              <div className="relative flex items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <span
                    className={[
                      "grid h-11 w-11 place-items-center rounded-2xl border shadow-[0_10px_24px_rgba(15,23,42,.06)]",
                      tone.icon,
                    ].join(" ")}
                  >
                    <ListChecks size={18} />
                  </span>

                  <div>
                    <div
                      className={["text-sm font-black", tone.text].join(" ")}
                    >
                      {group.label}
                    </div>

                    <div className="mt-1 text-xs font-semibold text-slate-500">
                      {group.items.length} incidencia(s) en este estado
                    </div>
                  </div>
                </div>

                <span
                  className={[
                    "inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-black shadow-[0_8px_20px_rgba(15,23,42,.045)]",
                    canReorder
                      ? `${tone.badge}`
                      : "border-slate-200 bg-white text-slate-400",
                  ].join(" ")}
                >
                  <GripHorizontal size={12} />
                  {canReorder ? "Reordenable" : "Tabla"}
                </span>
              </div>
            </header>

            <div
              className={[
                "overflow-x-auto bg-[radial-gradient(circle_at_top_left,rgba(0,208,156,.035),transparent_28%),#f8fafc] p-4",
                group.items.length > 7
                  ? "max-h-[620px] overflow-y-auto overscroll-contain"
                  : "",
              ].join(" ")}
              onDragOver={(event) => {
                if (!canReorder) return;
                event.preventDefault();
              }}
              onDrop={(event) => {
                if (!canReorder) return;
                event.preventDefault();
                if (event.target !== event.currentTarget) return;
                setDropTarget(null);
                void moveWithinGroup(group, null, "end");
              }}
            >
              <table className="it-table w-full min-w-[1180px] border-separate border-spacing-0">
                <thead>
                  <tr>
                    <th>Código</th>
                    <th>Título</th>
                    <th>Proyecto</th>
                    <th>Categoría</th>
                    <th>Solicitante</th>
                    <th>Responsable</th>
                    <th>Alta</th>
                  </tr>
                </thead>

                <tbody>
                  {group.items.map((incident) => (
                    <tr
                      key={incident.id}
                      draggable={canReorder}
                      className={[
                        "cursor-pointer transition-all duration-200",
                        tone.row,
                        dragState?.incidentId === incident.id
                          ? "opacity-70"
                          : "",
                      ].join(" ")}
                      onDragStart={() => {
                        if (!canReorder) return;

                        setDragState({
                          incidentId: incident.id,
                          groupKey: group.key,
                        });
                      }}
                      onDragEnd={() => {
                        setDragState(null);
                        setDropTarget(null);
                      }}
                      onDragOver={(event) => {
                        if (!canReorder) return;
                        event.preventDefault();

                        const rect =
                          event.currentTarget.getBoundingClientRect();

                        const position =
                          event.clientY < rect.top + rect.height / 2
                            ? "before"
                            : "after";

                        setDropTarget({
                          groupKey: group.key,
                          incidentId: incident.id,
                          position,
                        });
                      }}
                      onDrop={(event) => {
                        if (!canReorder) return;
                        event.preventDefault();
                        event.stopPropagation();

                        const rect =
                          event.currentTarget.getBoundingClientRect();

                        const position =
                          event.clientY < rect.top + rect.height / 2
                            ? "before"
                            : "after";

                        void moveWithinGroup(group, incident.id, position);
                      }}
                      onClick={() => onOpenIncident(incident)}
                      data-drop-position={
                        dropTarget?.groupKey === group.key &&
                        dropTarget.incidentId === incident.id
                          ? dropTarget.position
                          : undefined
                      }
                    >
                      <td>
                        <div className={["font-black", tone.text].join(" ")}>
                          {incident.codigo}
                        </div>

                        <div className="mt-1">
                          <span
                            className={[
                              "inline-flex rounded-full border px-2.5 py-1 text-[10px] font-black uppercase tracking-wide",
                              tone.badge,
                            ].join(" ")}
                          >
                            {getItStateLabel(incident.estado)}
                          </span>
                        </div>
                      </td>

                      <td>
                        <div className="max-w-[340px] font-black text-slate-950">
                          {incident.titulo}
                        </div>

                        <div className="mt-1 line-clamp-2 text-xs font-semibold leading-5 text-slate-500">
                          {incident.descripcion || "Sin descripción"}
                        </div>
                      </td>

                      <td>{incident.proyecto_nombre || "Sin proyecto"}</td>

                      <td>{incident.categoria || "Sin categoría"}</td>

                      <td>{incident.usuario_alta_nombre || "—"}</td>

                      <td>{incident.responsable_nombre || "Sin asignar"}</td>

                      <td>{formatItDateTime(incident.fecha_alta)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        );
      })}
    </div>
  );
}
